// src/Services/Auth/NativeAuthClient.ts
import { configService } from "../Config/ConfigService";

/** OAuth tokens we expect back from our API proxy */
export type NativeTokens = {
    access_token: string;
    id_token?: string;
    refresh_token?: string;
    expires_in: number;
    scope?: string;
    token_type: "Bearer";
};

/** Sign-in responses */
type InitiateResp = { continuation_token: string };
type ChallengeResp = {
    continuation_token: string;
    challenge_type?: "password" | "oob" | "redirect";
};

/** Reset (SSPR) polling status */
export type ResetPollStatus = "not_started" | "in_progress" | "succeeded" | "failed";

type ResetStartResp = { continuation_token: string };
type ResetChallengeResp = { continuation_token: string };
type ResetContinueResp = { continuation_token: string };
type ResetSubmitResp = { continuation_token: string; poll_interval?: number };
type ResetPollResp = {
    status: ResetPollStatus;
    poll_interval?: number;
    continuation_token?: string;
};

/** Shape of your env config we care about */
type EnvConfig = {
    PreCertspaIdentity?: {
        ApiScopes?: string[];
    };
    PreCertApiUrl?: string;
};

/** Helper to unwrap JSON errors (AAD-style) */
async function fetchJson(url: string, init: RequestInit): Promise<any> {
    const r = await fetch(url, init);
    const ct = r.headers.get("content-type") || "";
    const isJson = ct.includes("application/json") || ct.includes("text/json");

    if (!r.ok) {
        if (isJson) {
            const errBody = await r.json().catch(() => ({}));
            const msg =
                errBody?.error_description ||
                errBody?.message ||
                errBody?.error ||
                `${r.status} ${r.statusText}`;
            const e = new Error(msg) as any;
            if (errBody?.error) e.code = String(errBody.error);
            if (errBody?.error_codes?.[0]) e.aad_code = String(errBody.error_codes[0]);
            if (errBody?.suberror) e.suberror = String(errBody.suberror);
            throw e;
        } else {
            const txt = await r.text().catch(() => "");
            throw new Error(txt || `${r.status} ${r.statusText}`);
        }
    }

    return isJson ? r.json() : r.text();
}

export class NativeAuthClient {
    private base!: string;

    // Separate continuations so sign-in/reset flows don’t collide.
    private signInContinuationToken: string | null = null;
    private resetContinuationToken: string | null = null;

    private lastUsername: string | null = null;

    /** Initialize base URL from config (PreCertApiUrl) */
    private async init() {
        if (this.base) return;
        const cfg = (await configService.getConfiguration()) as EnvConfig;
        this.base = (cfg.PreCertApiUrl ?? "").replace(/\/+$/, "");
        if (!this.base) {
            throw new Error("NativeAuthClient: PreCertApiUrl is not configured.");
        }
    }

    /** Build default scope string from PreCertspaIdentity.ApiScopes or fallback */
    private async getDefaultScope(): Promise<string> {
        const cfg = (await configService.getConfiguration()) as EnvConfig;
        const apiScopes = cfg.PreCertspaIdentity?.ApiScopes ?? [];
        const scopes = apiScopes.length
            ? apiScopes
            : ["openid", "profile", "email", "offline_access"];
        return scopes.join(" ");
    }

    // ====================================================
    // ============== PASSWORD SIGN-IN (Native Auth) =======
    // ====================================================

    /**
     * Step 1: Kick off native sign-in for a username.
     * challenge_type should include whichever branches you support:
     *  - "password" for password
     *  - "oob" for OTP / email code
     *  - "redirect" if you want Entra to redirect to an IdP
     */
    async signInInitiate(
        username: string,
        challengeType = "password oob redirect"
    ): Promise<InitiateResp> {
        await this.init();
        this.lastUsername = username;

        const data = await fetchJson(`${this.base}/api/nativeauth/signin/initiate`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
            },
            body: JSON.stringify({ username, challenge_type: challengeType }),
        });

        this.signInContinuationToken = data?.continuation_token ?? null;
        return data as InitiateResp;
    }

    /**
     * Step 2: Ask which challenge to perform next (must use same/subset of the initial list).
     */
    async signInChallenge(
        continuationToken?: string,
        challengeType = "password oob redirect"
    ): Promise<ChallengeResp> {
        await this.init();
        const token = continuationToken ?? this.signInContinuationToken;
        if (!token) throw new Error("Missing continuation token.");

        const data = await fetchJson(`${this.base}/api/nativeauth/signin/challenge`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
            },
            body: JSON.stringify({
                continuation_token: token,
                challenge_type: challengeType,
            }),
        });

        this.signInContinuationToken = data?.continuation_token ?? this.signInContinuationToken;
        return data as ChallengeResp;
    }

    /**
     * Step 3 (password branch): Exchange the password for tokens.
     * You should call this after signInInitiate / signInChallenge indicate "password".
     */
    async signInTokenWithPassword(password: string): Promise<NativeTokens> {
        await this.init();
        if (!this.signInContinuationToken || !this.lastUsername) {
            throw new Error("Missing continuation token/username.");
        }
        const scope = await this.getDefaultScope();

        const data = await fetchJson(`${this.base}/api/nativeauth/signin/token`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
            },
            body: JSON.stringify({
                continuation_token: this.signInContinuationToken,
                password,
                scope,
            }),
        });

        this.signInContinuationToken = null;
        return data as NativeTokens;
    }

    /**
     * Use refresh_token to obtain a new access token.
     * AuthService calls this when it detects expiration.
     */
    async refresh(refresh_token: string): Promise<NativeTokens> {
        await this.init();
        const data = await fetchJson(`${this.base}/api/nativeauth/refresh`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
            },
            body: JSON.stringify({
                refresh_token,
                username: this.lastUsername,
            }),
        });
        return data as NativeTokens;
    }

    // ====================================================
    // ============== PASSWORD RESET (SSPR-like) ==========
    // ====================================================

    /** Step 1: begin reset for username */
    async ssprStart(username: string): Promise<ResetStartResp> {
        await this.init();
        this.lastUsername = username;

        const data = await fetchJson(`${this.base}/api/nativeauth/reset/start`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
            },
            body: JSON.stringify({
                username,
                challenge_type: "oob redirect", // include 'redirect' if using IdP
            }),
        });

        this.resetContinuationToken = data?.continuation_token ?? null;
        return data as ResetStartResp;
    }

    /** Step 2: request challenge (email OTP, etc.) */
    async ssprChallenge(): Promise<ResetChallengeResp> {
        await this.init();
        if (!this.resetContinuationToken) throw new Error("Missing continuation token.");

        const data = await fetchJson(`${this.base}/api/nativeauth/reset/challenge`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
            },
            body: JSON.stringify({
                continuation_token: this.resetContinuationToken,
                challenge_type: "oob redirect",
            }),
        });

        this.resetContinuationToken = data?.continuation_token ?? this.resetContinuationToken;
        return data as ResetChallengeResp;
    }

    /** Step 3: continue with received OTP code */
    async ssprContinueOtp(code: string): Promise<ResetContinueResp> {
        await this.init();
        if (!this.resetContinuationToken) throw new Error("Missing continuation token.");

        const data = await fetchJson(`${this.base}/api/nativeauth/reset/continue`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
            },
            body: JSON.stringify({
                continuation_token: this.resetContinuationToken,
                oob: code,
            }),
        });

        this.resetContinuationToken = data?.continuation_token ?? this.resetContinuationToken;
        return data as ResetContinueResp;
    }

    /** Step 4: submit the new password */
    async ssprSubmitNewPassword(new_password: string): Promise<ResetSubmitResp> {
        await this.init();
        if (!this.resetContinuationToken) throw new Error("Missing continuation token.");

        const data = await fetchJson(`${this.base}/api/nativeauth/reset/submit`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
            },
            body: JSON.stringify({
                continuation_token: this.resetContinuationToken,
                new_password,
            }),
        });

        this.resetContinuationToken = data?.continuation_token ?? this.resetContinuationToken;
        return data as ResetSubmitResp;
    }

    /** Step 5: poll completion */
    async ssprPollCompletion(): Promise<ResetPollResp> {
        await this.init();
        if (!this.resetContinuationToken) throw new Error("Missing continuation token.");

        const data = await fetchJson(`${this.base}/api/nativeauth/reset/poll`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
            },
            body: JSON.stringify({
                continuation_token: this.resetContinuationToken,
            }),
        });

        return data as ResetPollResp;
    }

    /**
     * Exchange reset continuation token for OAuth tokens
     * (e.g. automatically sign in the user after successful SSPR).
     */
    async tokenWithContinuation(
        continuation_token: string,
        username: string
    ): Promise<NativeTokens> {
        await this.init();
        this.lastUsername = username;
        const scope = await this.getDefaultScope();

        const data = await fetchJson(`${this.base}/api/nativeauth/token`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
            },
            body: JSON.stringify({
                grant_type: "continuation_token",
                continuation_token,
                username,
                scope,
            }),
        });

        this.resetContinuationToken = null;
        return data as NativeTokens;
    }
}
